<template>
    <slot name="header_close"/>
    <transition name="fade" mode="out-in">
        <div class="select-number-frame" v-if="!isAnotherPaySystem">
            <h2 class="titleSelect">{{ $t("page.tariffs.title_payment_system") }}:</h2>
            <div class="payment-type">
                <BaseRadio
                    v-model="selectedPaymentType"
                    value="one-time"
                    :label="$t('page.tariffs.payment_type.one_time')"
                />
                <BaseRadio
                    v-model="selectedPaymentType"
                    value="subscription"
                    :label="$t('page.tariffs.payment_type.subscription')"
                />
            </div>
            <ul class="payListSelect" :style="{ opacity: isLoading ? 0 : 1 }">
                <li
                    v-for="(elem, ind) in filteredPaymentSystems"
                    :key="ind"
                    class="payItemSelect"
                    @click="(event) => confirmPaymentSystem(elem, event)"
                >
                    <div class="card" :class="elem.name" :style="{ backgroundColor: elem.color }">
                        <img :class="elem.name" :src="elem.img" :width="elem.name === 'Visa/Masterсard' ? 110 : null"/>
                    </div>
                    <p class="subtitlePaySelect" :class="elem.name">
                        {{ elem.name }}
                    </p>
                </li>
            </ul>
            <Transition name="fade">
                <div v-if="isLoading" class="loading">
                    <span class="spinner"></span>
                </div>
            </Transition>
        </div>
        <component :is="AnotherPaymentDialog" v-else/>
    </transition>
</template>

<script setup>
// core
import {ref, computed, onMounted, onUnmounted} from "vue";
import {useI18n} from "vue-i18n";
import {useStore} from "vuex";
import {createToast, withProps, clearToasts} from "mosha-vue-toastify";

// components
import AnotherPaymentDialog from "@/components/Dialog/DialogComponents/AnotherPaymentDialog.vue";
import usePaymentTariffs from "@/components/Tariffs/api/usePaymentTariffs";
import ToastContent from "@/components/Recurring/ToastContent.vue";
import BaseRadio from "@/components/ui/BaseRadio.vue";

// data
import { usePaymentSystems } from "@/data/paymentSystems.js";

const {t: $t} = useI18n();
const store = useStore();
const paymentSystems = usePaymentSystems();

const isAnotherPaySystem = ref(false);
const selectedPaymentType = ref('one-time');
const selectedNumbers = computed(() => store.state.numbers.selectedNumbers);
const dialogParams = computed(() => store.state.dialogParams);
const filteredPaymentSystems = computed(() => {
    return selectedPaymentType.value === 'one-time'
        ? paymentSystems.value
        : paymentSystems.value.filter(sub => sub.canSubscriptions)
})
const isLoading = ref(false);

const confirmPaymentSystem = async (elem, event) => {
    if (elem.functionCall === "anotherPay") {
        isAnotherPaySystem.value = true;
    } else if (elem.functionCall === "getInvoice") {
        store.commit("setDialog", "InvoiceDialog");
        store.commit("openDialog", {
            name: "InvoiceDialog",
            params: {
                tariff: `Standard ${selectedNumbers.value?.count?.months}`,
                numbers: {
                    viber: selectedNumbers.value?.viber?.map(number => number.number),
                    telegram: selectedNumbers.value?.telegram?.map(number => number.number),
                    whatsapp: selectedNumbers.value?.whatsapp?.map(number => number.number),
                },
                amount: +dialogParams.value?.totalCost,
                is_subscription: selectedPaymentType.value === 'subscription'
            }
        });
    } else {
        isLoading.value = true;
        const payload = {
            product_name: `Standard ${selectedNumbers.value?.count?.months}`,
            numbers: {
                viber: selectedNumbers.value?.viber?.map(number => number.number),
                telegram: selectedNumbers.value?.telegram?.map(number => number.number),
                whatsapp: selectedNumbers.value?.whatsapp?.map(number => number.number),
            },
            month: `${selectedNumbers.value?.count?.months}`,
            amount: +dialogParams.value?.totalCost,
            is_subscription: selectedPaymentType.value === 'subscription'
        };

        const {getLinkToBill} = usePaymentTariffs(elem.link);

        await getLinkToBill(payload)
            .then((response) => {
                const link = response.data.redirect_url;
                if (link) {
                    store.commit("closeDialog");
                    window.open(link, "_self");
                }
            })
            .catch(() => {
                createToast(
                    withProps(ToastContent, {
                        msg: $t("page.tariffs.payment_error"),
                        type: "error",
                    }),
                    {
                        hideProgressBar: true,
                        timeout: 10000,
                    }
                );
            })
            .finally(() => {
                window.dataLayer.push({
                    event: "payment_click",
                    button_id: elem.name,
                });

                isLoading.value = false;
            });
    }
    store.state.paymentSystem = $t(elem.name);
};

const ensureCodeCountryLoaded = async () => {
    if (store.getters["auth/getCodeCountry"]) return;
    try {
        await store.dispatch("auth/getCodeCountry");
    } catch (error) {
        console.error("Failed to load code country:", error);
    }
};

onMounted(ensureCodeCountryLoaded);

onUnmounted(() => clearToasts());
</script>

<style lang="scss" scoped>
.loading {
    position: absolute;
    top: 54%;
    left: 50%;
    transform: translate(-50%, -50%);
    /* max-width: 650px;
      width: 100%;
      height: auto;
      padding: 28px 28px 0px; */
}

// the large PreloaderBase ring
.loading .spinner {
    width: 56px;
    height: 56px;
    margin: 0;
    border-width: 5px;
}

.payment-type {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
    margin-bottom: 16px;
}

.select-number-frame {
    z-index: 70;
    padding: 28px 28px 42px;
    box-sizing: border-box;
    border-radius: 7px;
    background-color: white;
    text-align: center;
    font-size: 14px;
    color: black;
    font-weight: 500;

    .title {
        text-transform: uppercase;
        color: black;

        span {
            font-size: 20px;
        }
    }

    .content {
        margin-top: 15px;

        display: flex;
        flex-direction: column;
        gap: 20px;

        text-align: left;
    }

    &:deep(button) {
        margin-top: 16px;
    }
}

.card {
    background: var(--block-color);
    width: 130px;
    height: 130px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;

    .svg,
    .png {
        position: static;
        transition: all 0.3s ease-in-out;
    }

    .svg {
        width: 90%;
    }
}

.titleSelect {
    font-family: "Gilroy";
    font-size: 18px;
    color: #404040;
    font-weight: 500;
    text-align: left;
    margin: 10px 0 20px;
    text-align: center;
    text-transform: uppercase;
}

.payListSelect {
    padding-left: 0;
    display: flex;
    justify-content: space-evenly;
    position: relative;
    gap: 10px;
    transition: all 0.4s ease-in-out;
}

.payItemSelect {
    width: 155px;
    height: 155px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    border: 2px solid transparent;
    border-radius: 5px;
    transition: all 0.4s ease-in-out;
    cursor: pointer;

    img {
        transition: all 0.4s ease-in-out;
    }

    :hover img {
        scale: 1.05;
    }
}

@media screen and (max-width: 740px) {
    .payListSelect {
        display: flex;
        flex-wrap: wrap;
    }

    .payItemSelect {
        height: 100%;
    }

    .logoPaySelect {
        height: 84px;
    }
}
</style>
